import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ClipboardList, Plus, Copy, Check, ExternalLink, Trash2, Power } from "lucide-react";
import api, { formatApiError, formatRelative } from "../../lib/api";
import Layout from "../../components/Layout";

const PUBLIC_BASE = window.location.origin;

export default function AdminSkjema() {
  const [forms, setForms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/screening/forms");
      setForms(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createForm = async () => {
    setError("");
    if (!newSlug.trim() || !newTitle.trim()) {
      setError("Slug og tittel er påkrevd");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/screening/forms", {
        slug: newSlug.trim().toLowerCase(),
        title: newTitle.trim(),
        description: newDesc.trim(),
        schema_key: "kvinne_v1",
      });
      setNewSlug(""); setNewTitle(""); setNewDesc("");
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (form) => {
    try {
      await api.patch(`/admin/screening/forms/${form.id}`, { is_active: !form.is_active });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (form) => {
    if (!window.confirm(`Slette skjemaet «${form.title}»? Innsendinger beholdes.`)) return;
    try {
      await api.delete(`/admin/screening/forms/${form.id}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const copyLink = async (form) => {
    const url = `${PUBLIC_BASE}/skjema/${form.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(form.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* fallback: manual */ }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-6 space-y-8">
        <div className="flex items-center gap-3">
          <Link to="/admin/board" className="font-meta text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="back-link">
            <ArrowLeft className="h-4 w-4 inline mr-1" /> Admin
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <ClipboardList className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)]">Skjema</h1>
        </div>

        <div className="border border-white/10 p-6 space-y-4" data-testid="new-form-block">
          <h2 className="font-fraunces italic text-xl text-[var(--cream)] flex items-center gap-2">
            <Plus className="h-4 w-4 text-[var(--magenta)]" /> Nytt skjema
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="font-meta text-xs text-[var(--cream-soft)] mb-1">Slug (i URL: /skjema/&lt;slug&gt;)</div>
              <input value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="f.eks. casting-2026" className="input-editorial w-full" data-testid="new-slug" />
            </div>
            <div>
              <div className="font-meta text-xs text-[var(--cream-soft)] mb-1">Tittel (vises øverst i skjemaet)</div>
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Spørreskjema kvinne" className="input-editorial w-full" data-testid="new-title" />
            </div>
          </div>
          <div>
            <div className="font-meta text-xs text-[var(--cream-soft)] mb-1">Beskrivelse (valgfritt)</div>
            <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
              rows={2} className="input-editorial w-full resize-y" data-testid="new-desc" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={createForm} disabled={creating} className="btn-magenta" data-testid="create-form-btn">
              {creating ? "Oppretter…" : "Opprett skjema"}
            </button>
            <span className="font-meta text-[10px] text-[var(--cream-faint)]">Schema: kvinne_v1 (fast)</span>
          </div>
        </div>

        {error && <div className="border-l-2 border-[var(--magenta)] pl-3 text-sm text-[var(--cream)] font-medium">{error}</div>}

        {loading && <div className="font-meta text-[var(--cream-soft)]">Laster…</div>}

        {!loading && forms && forms.length === 0 && (
          <div className="font-manrope text-sm text-[var(--cream-soft)] text-center py-10">
            Ingen skjema opprettet ennå.
          </div>
        )}

        {!loading && forms && forms.length > 0 && (
          <div className="space-y-3" data-testid="forms-list">
            {forms.map((f) => (
              <div key={f.id} className="border border-white/10 p-4 flex flex-wrap items-center gap-3" data-testid={`form-${f.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="font-fraunces italic text-lg text-[var(--cream)]">{f.title}</div>
                  <div className="font-meta text-[10px] text-[var(--cream-faint)]">
                    /skjema/{f.slug} · {f.submission_count || 0} svar · opprettet {formatRelative(f.created_at)}
                    {!f.is_active && <span className="ml-2 text-[var(--magenta)]">· inaktiv</span>}
                  </div>
                </div>
                <button onClick={() => copyLink(f)} className="btn-ghost text-xs" data-testid={`copy-${f.id}`}>
                  {copiedId === f.id ? <><Check className="h-3 w-3 inline mr-1" /> Kopiert</> : <><Copy className="h-3 w-3 inline mr-1" /> Kopier lenke</>}
                </button>
                <a href={`/skjema/${f.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs" data-testid={`preview-${f.id}`}>
                  <ExternalLink className="h-3 w-3 inline mr-1" /> Åpne
                </a>
                <Link to={`/admin/skjema/${f.id}`} className="btn-magenta text-xs" data-testid={`svar-${f.id}`}>
                  Se svar ({f.submission_count || 0})
                </Link>
                <button onClick={() => toggleActive(f)} className="btn-ghost text-xs" title="Aktiv/inaktiv" data-testid={`toggle-${f.id}`}>
                  <Power className="h-3 w-3" />
                </button>
                <button onClick={() => remove(f)} className="btn-ghost text-xs text-[var(--magenta)]" title="Slett" data-testid={`delete-${f.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
